import { useNavigate, useOutletContext } from "react-router-dom";
import { Button, List, ListItem, ListItemText } from '@mui/material';

import Page from "~/components/Page";
import MyList from "~/mui/MyList";
import EditProjectServicesModal from "~/modals/EditProjectServicesModal";
import ProjectApiCallModal from "~/modals/ProjectApiCallModal";
import ProjectNamesModal from "~/modals/ProjectNamesModal";
import AdminProjectInternalSettingsModal, { TAdminMessageType } from "~/modals/AdminProjectInternalSettingsModal";
import { classNames, formatDate, getServiceName } from "~/utils/utils";
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectAdminMessages, apiGetProjectAllServices, apiToggleProjectArchivation } from "~/providers/api";
import { IClientProject } from "~/providers/client-interfaces";
import { IOutlet, IPage } from "~/interfaces";
import { IAdminAdminMessages, IAdminProject } from "~/providers/admin-interfaces";
import { IService } from "~/providers/interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import PaperWithTitle from '~/components/PaperWithTitle';
import { MAX_YEAR } from "~/const";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import RemoveDialog from "~/modals/RemoveDialog";
import { getLink } from "~/providers/route";

import "./style.less";

interface IState {
	loading: boolean;
	isDirty: boolean;
	isVisitor: boolean;
	errorMsg: string;
	lastUpdate: string;
	project: IClientProject | IAdminProject;
	apiCallData: IClientProject | IAdminProject;
	namesData: IClientProject | IAdminProject;
	messageData: IAdminAdminMessages;
	internalSettings: IAdminProject;
	services: Array<IService>;
	editServices: boolean;
	showArchiveProject: boolean;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	isVisitor: true,
	errorMsg: "",
	lastUpdate: "",
	project: null,
	apiCallData: null,
	namesData: null,
	messageData: null,
	internalSettings: null,
	services: [],
	editServices: false,
	showArchiveProject: false,
};

export default function ProjectSettingsPage({
	isInternal = false,
}: IPage) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const navigate = useNavigate();

	function onSettingsUpdate(project: IClientProject | IAdminProject) {
		updateState({
			project,
			apiCallData: null,
			namesData: null,
			internalSettings: null,
			isDirty: false,
		});
	}

	// zavreme, nemame zmeny
	function onAdminMessage(type: TAdminMessageType, messageData?: IAdminAdminMessages) {
		updateState({
			internalSettings: null,
			isDirty: false,
			messageData: type === "add"
				? messageData
				: null,
		});
	}

	function onServicesSave(services: Array<IService>, lastUpdate: string) {
		setState(prev => {
			const project = prev.project;

			project.lastUpdate = lastUpdate;

			return {
				...prev,
				project,
				services,
				editServices: false,
				isDirty: false,
			};
		});
	}

	function onArchive(archived: boolean) {
		setState(prev => ({
			...prev,
			internalSettings: null,
			isDirty: false,
			project: {
				...prev.project,
				archived,
			},
		}));
	}

	function onDebtConsumption(allowWalletDebtConsumption: boolean, lastUpdate: string) {
		setState(prev => ({
			...prev,
			internalSettings: null,
			isDirty: false,
			project: {
				...prev.project,
				allowWalletDebtConsumption,
				lastUpdate,
			},
		}));
	}

	function getVisitedByClientAt() {
		return state.messageData
			? state.messageData.visitedByClientAt
				? formatDate(new Date(state.messageData.visitedByClientAt), isCzech)
				: trans("projectSettingsModal.tabs.noVisitedByClient")
			: "";
	}

	async function onProjectArchive(resolve: () => void, reject: (error: string) => void) {
		const archiveData = await apiToggleProjectArchivation(isInternal ? "admin" : "client", params.projectId, true, lang);

		if (archiveData.noErrors) {
			resolve();
			navigate(getLink("projects"));
		} else {
			reject(archiveData.errors.join(", "));
		}
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, servicesData, msgData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			isInternal
				? apiGetProjectAllServices(params.projectId, lang)
				: Promise.resolve({ noErrors: false, errors: [], data: [] }),
			isInternal
				? apiGetProjectAdminMessages("admin", params.projectId, lang)
				: Promise.resolve({ noErrors: false, errors: [], data: null }),
		]);

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
					lastUpdate: projectData.data.lastUpdate,
					isVisitor: !isInternal && projectData.data.userRole === "visitor",
				}
				: {},
			...servicesData.noErrors
				? {
					services: servicesData.data,
				}
				: {},
			...msgData.noErrors
				? {
					messageData: msgData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...servicesData.errors,
				...msgData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	/* eslint-disable no-magic-numbers */
	return <Page checkLogged={true} title={trans("projectSettingsPage.title")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("projectSettingsPage.title"))}
		checkInternalRole={isInternal ? "salesman" : "none"} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.settings">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ state.project && <div className="dashboardGrid">
			{ isInternal && <>
				<PaperWithTitle title={ trans("projectSettingsPage.internalTitle") } onClick={() => updateState({ internalSettings: state.project as IAdminProject })}>
					<List>
						<ListItem>
							<ListItemText primary={trans("projectSettingsPage.consumptionDebt.title")} secondary={trans((state.project as IAdminProject).allowWalletDebtConsumption ? "projectSettingsPage.consumptionDebt.enabled" : "projectSettingsPage.consumptionDebt.disabled")} />
						</ListItem>
						<ListItem>
							<ListItemText primary={state.project.internalName} secondary={trans("projectSettingsPage.idProject")} />
						</ListItem>
						{ state.project.validTill.indexOf(MAX_YEAR.toString()) === -1 && <ListItem>
							<ListItemText primary={formatDate(new Date(state.project.validTill), isCzech)} secondary={trans("createProject.validTill")} />
						</ListItem> }
						<ListItem>
							<ListItemText primary={"internalSwitch" in state.project && state.project.internalSwitch ? trans("projectSettingsPage.apiCallEnabled") : trans("projectSettingsPage.apiCallDisabled") }
								secondary={trans("projectSettingsPage.internalApiCall")} />
						</ListItem>
						{ state.messageData && <>
							<ListItem>
								<ListItemText primary={formatDate(new Date(state.messageData.createdAt), isCzech)} secondary={trans("projectSettingsModal.tabs.createdAt")} />
							</ListItem>
							<ListItem>
								<ListItemText primary={getVisitedByClientAt()} secondary={trans("projectSettingsModal.tabs.visitedByClientAt")} />
							</ListItem>
						</> }
						{ state.project.archived && <ListItem>
							<ListItemText primary={trans("projectSettingsPage.archivedProject")} />
						</ListItem> }
					</List>
				</PaperWithTitle>
				<PaperWithTitle title={ trans("projectSettingsPage.servicesTitle") } onClick={() => updateState({ editServices: true })} className="settingsPage__servicesPaper">
					<MyList items={state.services.map(service => ({ id: service.id, title: getServiceName(service) }))} endIcon={null} />
				</PaperWithTitle>
			</> }
			{ !isInternal && <PaperWithTitle title={ trans("projectSettingsPage.apiCallTitle") } onClick={state.isVisitor ? null : () => updateState({ apiCallData: state.project })}>
				<div className="settingsPage__apiCallCont">
					<span className="settingsPage__apiCallContTitle">
						{trans("projectSettingsPage.apiCall")}
					</span>
					<span className={classNames(["settingsPage__apiCallContDesc", state.project.userSwitch ? "green-text-color" : "red-text-color"])}>
						{ state.project.userSwitch ? trans("projectSettingsPage.apiCallEnabled") : trans("projectSettingsPage.apiCallDisabled") }
					</span>
					<span></span>
				</div>
				<p className="settingsPage__pWithMargin">
					{trans("projectSettingsPage.apiCallDesc")}
				</p>
			</PaperWithTitle> }
			{ !isInternal && <PaperWithTitle title={ trans("projectSettingsPage.nameTitle") } onClick={state.isVisitor ? null : () => updateState({ namesData: state.project })}>
				<List>
					<ListItem>
						<ListItemText primary={state.project.userTitle || trans("empty")} secondary={trans("projectSettingsPage.nameProject")} />
					</ListItem>
					<ListItem>
						<ListItemText primary={state.project.userDescription || trans("empty")} secondary={trans("projectSettingsPage.desc")} />
					</ListItem>
				</List>
			</PaperWithTitle> }
			{ (!state.isVisitor && !isInternal && !state.project?.archived) && <PaperWithTitle title={ trans("projectSettingsPage.archiveTitle") }>
				<div className="settingsPage__archiveProject">
					<Button onClick={() => updateState({ showArchiveProject: true })} color="success" variant="contained">
						{ trans("projectSettingsPage.archiveTitle") }
					</Button>
				</div>
			</PaperWithTitle> }
		</div> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} sx={{ marginTop: "var(--margin-large)" }} /> }
		{ state.apiCallData && <ProjectApiCallModal params={params} projectData={state.apiCallData} isInternal={isInternal}
			onClose={() => updateState({ apiCallData: null, isDirty: false })} onSave={onSettingsUpdate} /> }
		{ state.namesData && <ProjectNamesModal params={params} projectData={state.namesData} isInternal={isInternal} onChange={isDirty => updateState({ isDirty })}
			onClose={() => updateState({ namesData: null, isDirty: false })} onSave={onSettingsUpdate} /> }
		{ state.internalSettings && <AdminProjectInternalSettingsModal params={params} projectData={state.internalSettings} onChange={isDirty => updateState({ isDirty })} onDebtConsumption={onDebtConsumption}
			onClose={() => updateState({ internalSettings: null, isDirty: false })} onSave={onSettingsUpdate} onAdminMessage={onAdminMessage} messageData={state.messageData} onArchive={onArchive} /> }
		{ state.editServices && <EditProjectServicesModal services={state.services} params={params} lastUpdate={state.project.lastUpdate}
			onClose={() => updateState({ editServices: false, isDirty: false })} onSave={onServicesSave} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.showArchiveProject && <RemoveDialog title={trans("projectSettingsPage.archiveTitle")} desc={trans("projectSettingsPage.archiveDesc")}
			onClose={() => updateState({ showArchiveProject: false })} onRemove={onProjectArchive} removeCaption={trans("projectSettingsPage.archiveTitle")} /> }
	</Page>;
}
